import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { InfoCircleOutlined } from '@ant-design/icons';
import PriceBoard from '@/components/PriceBoard';

const HomeContainer = styled.div`
  min-height: calc(100vh - 200px);
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(
      circle at 25% 25%,
      rgba(239, 179, 72, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 75% 75%,
      rgba(239, 179, 72, 0.05) 0%,
      transparent 50%
    );
  pointer-events: none;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 4rem 2rem 2rem;
  position: relative;
  z-index: 1;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 600;
  color: #efb348;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.h2`
  font-size: clamp(1.2rem, 3vw, 1.8rem);
  font-weight: 400;
  color: #efb348;
  margin-bottom: 0.5rem;
  opacity: 0.9;
`;

// const Description = styled.p`
//   font-size: 1.1rem;
//   color: rgba(255, 255, 255, 0.8);
//   max-width: 800px;
//   margin: 2rem auto;
//   line-height: 1.6;
// `;

const blink = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const NegotiableNotice = styled.div`
  text-align: center;
  color: #efb348;
  margin: 2rem 0;
  animation: ${blink} 3s linear infinite;
  font-size: 1rem;

  .anticon {
    margin-right: 8px;
  }
`;

const ContactSection = styled.section`
  background: rgba(0, 0, 0, 0.7);
  margin: 2rem auto;
  max-width: 800px;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;

  h3 {
    margin-bottom: 1rem;
    color: #efb348;
  }

  p {
    margin-bottom: 1rem;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
  }

  a {
    color: #42a5f5;
    text-decoration: none;

    &:hover {
      color: #81d4fa;
      text-decoration: underline;
    }
  }
`;

const BannerImage = styled.img`
  display: block;
  margin: 2rem auto 0;
  max-width: 1000px;
  width: 100%;
  border-radius: 16px;

  @media (max-width: 768px) {
    max-width: 95%;
    margin: 1rem auto 0;
  }

  @media (max-width: 480px) {
    max-width: 98%;
    border-radius: 12px;
  }
`;

const BannerText = styled.div`
  margin: 1rem auto 2rem;
  max-width: 1000px;
  width: 100%;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 16px;
  padding: 1rem;
  text-align: left;

  h2 {
    margin-bottom: 0.5rem;
    color: #efb348;
    font-weight: 600;
    font-size: clamp(1rem, 1.5vw, 2rem);
    line-height: 1.3;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
  }

  p {
    margin-bottom: 0.5rem;
    color: white;
    font-weight: 400;
    font-size: clamp(1rem, 2.5vw, 1.2rem);
    line-height: 1.6;
  }

  .payment-icons {
    display: flex;
    gap: 1rem;
    align-items: center;
    margin-top: 1rem;

    img {
      width: auto;
      height: 40px;
      filter: brightness(1.1);
      transition: transform 0.3s ease, filter 0.3s ease;

      &:hover {
        transform: scale(1.05);
        filter: brightness(1.3);
      }
    }
  }

  @media (max-width: 768px) {
    max-width: 95%;
    margin: 1rem auto 1.5rem;
    padding: 1.5rem;
    border-radius: 12px;

    h2 {
      margin-bottom: 0.8rem;
    }

    .payment-icons {
      img {
        height: 35px;
      }
    }
  }

  @media (max-width: 480px) {
    max-width: 98%;
    padding: 1rem;

    .payment-icons {
      gap: 0.8rem;

      img {
        height: 30px;
      }
    }
  }
`;

const Home = () => {
  const { t } = useTranslation();

  return (
    <HomeContainer>
      <BackgroundPattern />

      <BannerImage src="/images/audd_banner_new.gif" alt="AUDD Banner" />

      <BannerText>
        <h2>✅{t('home.bannerText.title')}</h2>
        {/* <p>{t('home.bannerText.subtitle')}</p> */}
        <p>{t('home.bannerText.description')}</p>
        <div className="payment-icons">
          <img src="/images/icons/unionpay.svg" alt="UnionPay" />
          <img src="/images/icons/wechatpay.svg" alt="WeChat Pay" />
          <img src="/images/icons/alipay.svg" alt="Alipay" />
        </div>
      </BannerText>

      <HeroSection>
        <Title>{t('home.title')}</Title>
        {/* <Subtitle>{t('home.subtitle1')}</Subtitle> */}
        <Subtitle>{t('home.subtitle2')}</Subtitle>

        {/* <Description>{t('home.description')}</Description> */}
      </HeroSection>

      <PriceBoard />

      <NegotiableNotice>
        <InfoCircleOutlined />
        {t('home.negotiableNotice')}
      </NegotiableNotice>

      <ContactSection>
        <h3>{t('home.contactSection.title')}</h3>
        <p>{t('home.contactSection.description')}</p>
        <p>
          {t('home.contactSection.email')}{' '}
          <a href="mailto:support@sydgoldx.com.au">support@sydgoldx.com.au</a>
        </p>
      </ContactSection>
    </HomeContainer>
  );
};

export default Home;
